import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type ColumnFiltersState,
  type OnChangeFn,
  type SortingState,
  type Table,
  type Updater,
} from '@tanstack/react-table';
import { useState } from 'react';
import { useDelayedLoading } from '@avanta/shared/hooks/useDelayedLoading';
import { defaultColumnFilterFn } from '@avanta/shared/utils/table';

type PaginationState = { pageIndex: number; pageSize: number };

interface SettledSnapshot<T> {
  data: T[];
  totalCount: number;
  pageCountKey: string;
}

export interface UseServerPaginatedTableProps<T> {
  columns: ColumnDef<T>[];
  data: T[];
  totalCount: number;
  pagination: PaginationState;
  onPaginationChange: (updater: Updater<PaginationState>) => void;
  globalFilter: string;
  onGlobalFilterChange: (updater: Updater<string>) => void;
  columnFilters?: ColumnFiltersState;
  onColumnFiltersChange?: OnChangeFn<ColumnFiltersState>;
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  manualSorting?: boolean;
  isLoadingData?: boolean;
  pageCountKey?: unknown;
}

const isSameSnapshot = <T>(first: SettledSnapshot<T>, second: SettledSnapshot<T>): boolean =>
  first.totalCount === second.totalCount &&
  first.pageCountKey === second.pageCountKey &&
  first.data.length === second.data.length &&
  first.data.every((item, index) => Object.is(item, second.data[index]));

export const useServerPaginatedTable = <T>({
  columns,
  data,
  totalCount,
  pagination,
  onPaginationChange,
  globalFilter,
  onGlobalFilterChange,
  columnFilters,
  onColumnFiltersChange,
  sorting: controlledSorting,
  onSortingChange,
  manualSorting = false,
  isLoadingData = false,
  pageCountKey,
}: UseServerPaginatedTableProps<T>): Table<T> => {
  const [uncontrolledSorting, setUncontrolledSorting] = useState<SortingState>([]);
  const sorting = controlledSorting ?? uncontrolledSorting;

  const current: SettledSnapshot<T> = {
    data,
    totalCount,
    pageCountKey: JSON.stringify([pageCountKey, pagination.pageSize]),
  };
  const [settled, setSettled] = useState(current);

  if (!isLoadingData && !isSameSnapshot(settled, current)) {
    setSettled(current);
  }

  const visible = isLoadingData ? settled : current;
  const pageCount =
    visible.totalCount > 0 ? Math.ceil(visible.totalCount / pagination.pageSize) : 0;
  const showDelayedLoading = useDelayedLoading(isLoadingData);
  const isLoading = isLoadingData && (showDelayedLoading || settled.data.length === 0);
  const isPageCountChanging = isLoadingData && settled.pageCountKey !== current.pageCountKey;
  const showDelayedPageCountLoading = useDelayedLoading(isPageCountChanging);
  const isPageCountLoading = isPageCountChanging && showDelayedPageCountLoading;

  return useReactTable({
    columns,
    data: visible.data,
    defaultColumn: {
      enableSorting: false,
      filterFn: defaultColumnFilterFn,
    },
    manualPagination: true,
    manualFiltering: true,
    manualSorting,
    pageCount,
    rowCount: visible.totalCount,
    meta: { isLoading, isPageCountLoading },
    state: {
      pagination,
      globalFilter,
      columnFilters,
      sorting,
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onGlobalFilterChange: onGlobalFilterChange,
    onColumnFiltersChange,
    onSortingChange: onSortingChange ?? setUncontrolledSorting,
    onPaginationChange: onPaginationChange,
  });
};
